import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  MapPin,
  Wrench,
  FileText,
  Bell,
  User,
  X,
  Flame,
  Building2,
  Truck,
  ClipboardList,
  Archive,
  Package,
  Database,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminSidebarNav from './admin/AdminSidebarNav';

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const { isAdmin } = useAuth();

  const adminSections = [
    {
      title: 'Général',
      items: [{ name: 'Tableau de bord', icon: LayoutDashboard, path: '/dashboard' }],
    },
    {
      title: 'Clients & parc',
      items: [
        { name: 'Clients', icon: Users, path: '/clients' },
        { name: 'Sites', icon: Building2, path: '/admin/sites' },
        { name: 'Équipements', icon: Package, path: '/equipment' },
      ],
    },
    {
      title: 'Exploitation',
      items: [
        { name: 'Plans', icon: MapPin, path: '/plans' },
        { name: 'Maintenances', icon: Wrench, path: '/maintenance' },
        { name: 'Rapports', icon: FileText, path: '/reports' },
      ],
    },
    {
      title: 'Technique',
      items: [
        { name: 'Emplacement Armoires', icon: Archive, path: '/admin/cabinet-placements' },
        { name: 'Équipements Véhicules', icon: Truck, path: '/admin/vehicle-equipment' },
        { name: 'État Extincteurs', icon: ClipboardList, path: '/admin/extinguisher-inventory' },
      ],
    },
    {
      title: 'Compte',
      items: [
        { name: 'Notifications', icon: Bell, path: '/notifications' },
        { name: 'Mon Profil', icon: User, path: '/admin-profile' },
        { name: 'Sauvegardes', icon: Database, path: '/admin-profile/backups' },
      ],
    },
  ];

  const clientMenuItems = [
    { name: 'Tableau de bord', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'Mes Sites', icon: Building2, path: '/my-sites' },
    { name: 'Mon Plan', icon: MapPin, path: '/my-plan' },
    { name: 'Mes Rapports', icon: FileText, path: '/my-reports' },
    { name: 'Notifications', icon: Bell, path: '/notifications' },
    { name: 'Emplacement Armoires', icon: Archive, path: '/technical/cabinet-placements' },
    { name: 'Équipements Véhicules', icon: Truck, path: '/technical/vehicle-equipment' },
    { name: 'État Extincteurs', icon: ClipboardList, path: '/technical/extinguisher-inventory' },
    { name: 'Mon Profil', icon: User, path: '/profile' },
  ];

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      <aside
        className={`fixed inset-y-0 left-0 z-50 border-r border-gray-200 bg-white transition-all duration-300 ease-in-out lg:static lg:shrink-0 ${
          sidebarOpen
            ? 'w-64 translate-x-0'
            : 'w-64 -translate-x-full lg:w-20 lg:translate-x-0'
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div
            className={`flex items-center border-b border-gray-200 bg-white ${
              sidebarOpen ? 'justify-between px-6 py-5' : 'justify-center px-2 py-4 lg:px-0'
            }`}
          >
            <div className={`flex items-center ${sidebarOpen ? 'gap-3' : 'justify-center'}`}>
              <div
                className={`flex shrink-0 items-center justify-center rounded-xl bg-red-600 ${
                  sidebarOpen ? 'h-12 w-12' : 'h-10 w-10'
                }`}
              >
                <Flame className={`text-white ${sidebarOpen ? 'h-7 w-7' : 'h-6 w-6'}`} />
              </div>
              {sidebarOpen && (
                <div>
                  <h1 className="text-lg font-bold text-gray-900">PETROFEU</h1>
                  <p className="text-xs text-gray-600">Gestion incendie</p>
                </div>
              )}
            </div>
            {sidebarOpen && (
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 lg:hidden"
                aria-label="Fermer le menu"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Navigation */}
          {isAdmin ? (
            <AdminSidebarNav
              sections={adminSections}
              sidebarOpen={sidebarOpen}
              onNavigate={handleNavClick}
            />
          ) : (
            <nav className={`flex-1 overflow-y-auto py-4 ${sidebarOpen ? 'px-3' : 'px-2'}`}>
              {clientMenuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={handleNavClick}
                  title={!sidebarOpen ? item.name : undefined}
                  className={({ isActive }) =>
                    `mb-1 flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      sidebarOpen ? 'gap-3' : 'justify-center'
                    } ${
                      isActive
                        ? 'bg-red-50 text-red-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className="h-5 w-5 shrink-0" strokeWidth={isActive ? 2 : 1.5} />
                      {sidebarOpen && <span>{item.name}</span>}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          )}

          {/* Footer */}
          {sidebarOpen && (
            <div className="border-t border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-600">
                © {new Date().getFullYear()} PETROFEU ÉQUIPEMENTS
              </p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
